import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from './Badge';
import { Input } from './Input';
import { Label } from './Label';
import { ChevronDown, X, Loader2 } from 'lucide-react';

// Option type: string or object
export type ChipOption = string | { id?: string|number; label: string; value: string; [key: string]: any };

interface ChipInputProps {
  label?: string;
  value: ChipOption[] | ChipOption | null;
  onChange: (value: ChipOption[] | ChipOption | null) => void;
  options?: ChipOption[];
  allowCustom?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  multi?: boolean;
  getOptionLabel?: (option: ChipOption) => string;
  getOptionValue?: (option: ChipOption) => string;
  isLoading?: boolean;
  className?: string;
  showDropdown?: boolean;
}

const defaultGetOptionLabel = (option: ChipOption) => typeof option === 'string' ? option : option.label;
const defaultGetOptionValue = (option: ChipOption) => typeof option === 'string' ? option : option.value;

export const ChipInput: React.FC<ChipInputProps> = ({
  label,
  value,
  onChange,
  options = [],
  allowCustom = false,
  placeholder = '',
  disabled = false,
  required = false,
  multi = true,
  getOptionLabel = defaultGetOptionLabel,
  getOptionValue = defaultGetOptionValue,
  isLoading = false,
  className = '',
  showDropdown = true,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number, left: number, width: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Normalize value for multi/single select
  const selectedArray: ChipOption[] = multi
    ? (Array.isArray(value) ? value : value ? [value] : [])
    : [];
  const selectedSingle: ChipOption | null = !multi
    ? (!Array.isArray(value) ? value : value && value.length > 0 ? value[0] : null)
    : null;

  // Filter options to exclude already-selected
  const filteredOptions = options.filter(opt => {
    const optVal = getOptionValue(opt);
    if (multi) {
      return !selectedArray.some(sel => getOptionValue(sel) === optVal);
    } else {
      return !selectedSingle || getOptionValue(selectedSingle) !== optVal;
    }
  }).filter(opt =>
    getOptionLabel(opt).toLowerCase().includes(inputValue.toLowerCase())
  );

  // Update dropdown position
  const updateDropdownPosition = () => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  // Update position when dropdown opens
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      
      // Update position on scroll and resize
      const handleUpdate = () => updateDropdownPosition();
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [isOpen]);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedOutsideInput = containerRef.current && !containerRef.current.contains(target);
      const clickedOutsideDropdown = listRef.current && !listRef.current.contains(target);
      
      if (clickedOutsideInput && clickedOutsideDropdown) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && inputValue === '' && multi && selectedArray.length > 0) {
      // Remove last chip
      onChange(selectedArray.slice(0, -1));
      return;
    }
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < filteredOptions.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : filteredOptions.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        } else if (allowCustom && inputValue.trim()) {
          handleAddCustom(inputValue.trim());
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Add custom value as chip
  const handleAddCustom = (val: string) => {
    if (!val) return;
    if (multi) {
      onChange([...selectedArray, val]);
    } else {
      onChange(val);
    }
    setInputValue('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Select from dropdown
  const handleSelect = (option: ChipOption) => {
    if (multi) {
      onChange([...selectedArray, option]);
    } else {
      onChange(option);
    }
    setInputValue('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Remove chip
  const handleRemove = (idx: number) => {
    if (multi) {
      onChange(selectedArray.filter((_, i) => i !== idx));
    } else {
      onChange(null);
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  // Handle input blur (delay to allow click)
  const handleBlur = () => {
    setTimeout(() => setIsOpen(false), 150);
  };

  // Add custom chip on comma or enter
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (allowCustom && (e.key === 'Enter' || e.key === ',')) {
      e.preventDefault();
      if (inputValue.trim()) {
        handleAddCustom(inputValue.trim());
      }
    } else {
      handleKeyDown(e);
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      onClick={() => {
        if (!disabled && inputRef.current) {
          inputRef.current.focus();
          setIsOpen(true);
        }
      }}
    >
      {label && (
        <Label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}
      <div
        className="flex flex-wrap items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white focus-within:border-blue-400 transition-colors cursor-text"
        tabIndex={0}
        onClick={e => {
          // Prevent container click from bubbling to parent
          if (e.target === e.currentTarget && inputRef.current) {
            inputRef.current.focus();
            setIsOpen(true);
          }
        }}
        style={{ boxShadow: 'none', outline: 'none' }}
      >
        {multi && selectedArray.map((chip, idx) => (
          <Badge key={idx} className="flex items-center gap-1 pr-1 bg-blue-100 text-blue-800 border border-blue-300 font-semibold px-3 py-1 rounded-full shadow-sm">
            <span>{getOptionLabel(chip)}</span>
            <button
              type="button"
              className="ml-1 p-0.5 rounded hover:bg-blue-200"
              onClick={e => {
                e.stopPropagation();
                handleRemove(idx);
              }}
              tabIndex={-1}
            >
              <X size={14} />
            </button>
          </Badge>
        ))}
        {!multi && selectedSingle && (
          <Badge className="flex items-center gap-1 pr-1 bg-gray-100">
            <span>{getOptionLabel(selectedSingle)}</span>
            <button
              type="button"
              className="ml-1 p-0.5 rounded hover:bg-gray-200"
              onClick={() => handleRemove(0)}
              tabIndex={-1}
            >
              <X size={14} />
            </button>
          </Badge>
        )}
        <input
          ref={inputRef}
          type="text"
          className="flex-1 min-w-[120px] border-none outline-none bg-transparent text-gray-900 placeholder-gray-400 focus:ring-0 focus:outline-none p-0 m-0"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={handleBlur}
          onKeyDown={handleInputKeyDown}
          placeholder={selectedArray.length === 0 ? placeholder : ''}
          disabled={disabled}
          style={{ boxShadow: 'none', outline: 'none' }}
        />
        {/* Arrow button */}
        {showDropdown ? (
          <button
            type="button"
            tabIndex={-1}
            className="ml-auto px-1 text-gray-400 hover:text-gray-600 focus:outline-none bg-transparent"
            onClick={e => {
              e.stopPropagation();
              if (!disabled && inputRef.current) {
                inputRef.current.focus();
                setIsOpen(true);
              }
            }}
          >
            <ChevronDown size={20} />
          </button>
        ) : null}
      </div>
      
      {/* Portal-rendered dropdown */}
      {showDropdown && isOpen && typeof window !== 'undefined' && dropdownPosition && (filteredOptions.length > 0 || allowCustom || isLoading) && 
        createPortal(
          <ul
            ref={listRef}
            className="absolute z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto py-1"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              position: 'absolute',
            }}
          >
            {isLoading ? (
              <li className="flex items-center justify-center py-2 text-gray-500">
                <Loader2 className="animate-spin mr-2" size={16} /> Loading...
              </li>
            ) : filteredOptions.length === 0 && !allowCustom ? (
              <li className="py-2 px-4 text-gray-500">No options</li>
            ) : (
              filteredOptions.map((option, idx) => (
                <li
                  key={getOptionValue(option)}
                  className={`px-4 py-2 cursor-pointer hover:bg-blue-50 ${highlightedIndex === idx ? 'bg-blue-100' : ''}`}
                  onMouseDown={e => {
                    e.preventDefault();
                    handleSelect(option);
                  }}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                >
                  {getOptionLabel(option)}
                </li>
              ))
            )}
            {/* Allow custom entry */}
            {allowCustom && inputValue.trim() && !filteredOptions.some(opt => getOptionLabel(opt).toLowerCase() === inputValue.trim().toLowerCase()) && (
              <li
                className="px-4 py-2 cursor-pointer text-blue-600 hover:bg-blue-50"
                onMouseDown={e => {
                  e.preventDefault();
                  handleAddCustom(inputValue.trim());
                }}
              >
                Add "{inputValue.trim()}"
              </li>
            )}
          </ul>,
          document.body
        )
      }
    </div>
  );
};

export default ChipInput;